
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-4">
              <img src="https://public.readdy.ai/ai/img_res/371caaa0-da3e-499a-ac42-780d1dfe63dc.png" alt="CECAL LTDA Logo" className="h-12 w-12 object-contain" />
              <h1 className="text-xl font-medium text-[#2B2B2B] tracking-wide">CECAL LTDA</h1>
            </div>
            <nav className="flex items-center gap-12">
              <button onClick={() => scrollToSection('home')} className="text-xs uppercase tracking-wider text-[#2B2B2B] hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-xs uppercase tracking-wider text-[#2B2B2B] hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-xs uppercase tracking-wider text-[#2B2B2B] hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('contact')} className="text-xs uppercase tracking-wider text-[#2B2B2B] hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/5 via-transparent to-[#14B8A6]/5"></div>
        <div className="max-w-[1400px] mx-auto px-20 relative">
          <div className="flex items-center gap-20">
            <div className="flex-1">
              <div className="text-xs text-[#8B7E74] mb-8 tracking-wider">/ iOS Development</div>
              <h2 className="text-7xl font-extralight text-[#1A1614] leading-tight mb-8">
                Modern Applications<br />
                for Enhanced Mobile<br />
                Experience &amp; Security
              </h2>
              <p className="text-base text-[#6B6B6B] max-w-[480px] leading-relaxed">
                Specialized in VPN and Cleaner solutions that prioritize user privacy and device optimization. Our expert team develops cutting-edge iOS applications designed to enhance security and improve performance.
              </p>
              <div className="flex items-center gap-4 mt-16">
                <div className="flex items-center -space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8C61] border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#14B8A6] to-[#2DD4BF] border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B7E74] to-[#A39990] border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] border-2 border-white"></div>
                </div>
                <span className="text-sm text-[#6B6B6B]">Expert Development Team</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/174f5d573ee0c841bbf2b5d7d6c8745d.jpg" 
                  alt="iOS Application Interface" 
                  className="w-full h-auto rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="flex items-center gap-0">
            <div className="flex-1 bg-[#F0EDE6] rounded-3xl overflow-hidden relative h-[700px]">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20software%20development%20workspace%20with%20multiple%20monitors%20displaying%20iOS%20application%20code%20and%20design%20interfaces%2C%20clean%20minimalist%20office%20environment%20with%20natural%20lighting%2C%20professional%20tech%20team%20collaboration%20space%2C%20contemporary%20workspace%20aesthetic%20with%20plants%20and%20organized%20desk%20setup&width=700&height=700&seq=about-workspace&orientation=squarish" 
                alt="Development Workspace" 
                className="w-full h-full object-cover"
              />
              <div className="absolute top-8 left-8 text-xs text-[#2B2B2B] tracking-wider">/ Our Approach</div>
              <div className="absolute bottom-20 left-8">
                <h3 className="text-7xl font-bold text-white leading-tight">
                  Expert iOS<br />
                  Development<br />
                  Solutions
                </h3>
              </div>
            </div>
            <div className="flex-1 bg-white flex items-center justify-center px-20">
              <div className="max-w-[420px] text-center">
                <h3 className="text-5xl font-bold text-[#1A1614] leading-tight mb-12">
                  Specialized in<br />Mobile Security
                </h3>
                <p className="text-base text-[#6B6B6B] leading-relaxed">
                  At CECAL LTDA, we are a dedicated team of experts focused on developing modern applications for the iOS platform. Our flagship products, including VPN and Cleaner applications, are meticulously designed to enhance user experience while significantly improving the security and performance of mobile devices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-[#1A1614] mb-4">Our Core Products 🛡️</h2>
            <p className="text-lg text-[#6B6B6B]">Innovative solutions for iOS security and optimization</p>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <ProductCard
              title="VPN Application"
              description="Secure your online presence with our advanced VPN solution. Featuring military-grade encryption, global server network, and lightning-fast connection speeds to protect your privacy anywhere."
              image="https://readdy.ai/api/search-image?query=Premium%20VPN%20application%20interface%20on%20iPhone%20screen%20showing%20world%20map%20with%20connection%20nodes%2C%20dark%20blue%20gradient%20background%20with%20glowing%20network%20lines%2C%20secure%20shield%20icon%2C%20modern%20minimalist%20UI%20design%20with%20connection%20status%20indicators%20and%20server%20selection%20menu&width=700&height=900&seq=product-vpn&orientation=portrait"
              gradient="from-[#1E3A8A]/90 to-[#3B82F6]/90"
            />
            <ProductCard
              title="Cleaner Application"
              description="Optimize your device performance with intelligent cleaning technology. Remove junk files, manage storage efficiently, and boost your iPhone's speed with our comprehensive cleaning solution."
              image="https://readdy.ai/api/search-image?query=Modern%20iPhone%20cleaner%20app%20interface%20displaying%20storage%20analysis%20with%20colorful%20circular%20charts%2C%20gradient%20purple%20to%20pink%20background%2C%20clean%20minimalist%20design%20showing%20file%20categories%20and%20optimization%20options%2C%20professional%20app%20screenshot%20with%20smooth%20gradients&width=700&height=900&seq=product-cleaner&orientation=portrait"
              gradient="from-[#7C3AED]/90 to-[#EC4899]/90"
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="bg-[#0A0A0A] rounded-3xl p-16">
            <div className="grid grid-cols-12 gap-16 mb-16">
              <div className="col-span-6">
                <h2 className="text-6xl font-thin text-white mb-6">CECAL LTDA</h2>
                <p className="text-sm text-[#999] max-w-[420px] leading-relaxed mb-12">
                  Modern iOS applications for enhanced security and performance. Specializing in VPN and Cleaner solutions.
                </p>
              </div>
              <div className="col-span-3">
                <h3 className="text-xs uppercase tracking-wider text-[#666] mb-6">Explore</h3>
                <div className="space-y-4">
                  <button onClick={() => scrollToSection('products')} className="block text-sm text-white hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Products</button>
                  <button onClick={() => scrollToSection('about')} className="block text-sm text-white hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">About</button>
                  <button onClick={() => scrollToSection('contact')} className="block text-sm text-white hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Contact</button>
                </div>
              </div>
              <div className="col-span-3">
                <h3 className="text-xs uppercase tracking-wider text-[#666] mb-6">Reach Us</h3>
                <div className="space-y-3 text-sm text-[#CCC]">
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-[#FF6B35] mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                    <span>+1 3253997021</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-[#FF6B35] mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                    <span>information@cecalltda.com</span>
                  </div>
                  <div className="mt-8">
                    <h4 className="text-xs uppercase tracking-wider text-[#666] mb-4">Location</h4>
                    <div className="space-y-2 text-sm text-[#CCC]">
                      <div className="flex items-start gap-3">
                        <i className="ri-map-pin-line text-[#FF6B35] mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                        <div>
                          <div>CARRERA 8 41 55</div>
                          <div>MONTERIA, 230001</div>
                          <div>Colombia</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-[#333] pt-8 flex items-center justify-between">
              <div className="text-sm text-[#666]">© 2025 CECAL LTDA. All rights reserved</div>
              <div className="text-sm text-[#666]">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B35] transition-colors">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ title, description, image, gradient }: { title: string; description: string; image: string; gradient: string }) {
  return (
    <div className="group relative rounded-3xl overflow-hidden h-[600px] cursor-pointer transition-all duration-500 hover:scale-[1.02]">
      <div className="absolute inset-0">
        <img src={image} alt={title} className="w-full h-full object-cover" />
      </div>
      <div className={`absolute inset-0 bg-gradient-to-t ${gradient}`}></div>
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <h3 className="text-3xl font-bold text-white mb-4">{title}</h3>
        <p className="text-sm text-white/90 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
